import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { SyncLogTypes } from "models/sync-log"
import { fetchMostRecentSyncLog } from "slices/sync-log/thunk"
import { setSyncLog } from "slices/sync-log/reducer"

interface UseSyncProcessOptions {
    // Sync log the Lambda writes when it finishes
    module: SyncLogTypes
    // Calls the API that starts the sync; true when it was triggered
    start: () => Promise<boolean>
    // Runs once the new sync log appears (reload the page data here)
    onFinished?: () => void | Promise<void>
    intervalMs?: number
    timeoutMs?: number
}

const logTime = (log?: { syncedAt?: Date | string | null } | null) =>
    log?.syncedAt ? new Date(log.syncedAt).getTime() : 0

// Sync buttons start a Lambda that runs in the background (fire and forget). This hook starts it,
// polls GET /sync-log/:module until a newer finished log shows up, then refreshes the page.
const useSyncProcess = ({
    module,
    start,
    onFinished,
    intervalMs = 15000,
    timeoutMs = 20 * 60 * 1000,
}: UseSyncProcessOptions) => {
    const dispatch = useDispatch<any>()
    const [isSyncing, setIsSyncing] = useState(false)
    const timer = useRef<ReturnType<typeof setInterval> | null>(null)
    const onFinishedRef = useRef(onFinished)
    onFinishedRef.current = onFinished

    const stopPolling = () => {
        if (timer.current) clearInterval(timer.current)
        timer.current = null
    }

    useEffect(() => stopPolling, [])

    const startSync = useCallback(async () => {
        if (timer.current) return
        setIsSyncing(true)

        const baseline = await fetchMostRecentSyncLog(module)
        const started = await start()
        if (!started) {
            setIsSyncing(false)
            return
        }

        const startedAt = Date.now()
        timer.current = setInterval(async () => {
            const log = await fetchMostRecentSyncLog(module)
            const isNew = !!log && logTime(log) > logTime(baseline) && !!log.finishedAt

            if (isNew) {
                stopPolling()
                dispatch(setSyncLog({ moduleName: module, data: log! }))
                await onFinishedRef.current?.()
                setIsSyncing(false)
                toast.success(onFinishedRef.current
                    ? "Sync finished. The page has been refreshed."
                    : "Sync finished. Refresh the page to see the new data.")
                return
            }

            if (Date.now() - startedAt > timeoutMs) {
                stopPolling()
                setIsSyncing(false)
                toast.info("The sync is taking longer than expected. Refresh the page later to see the new data.")
            }
        }, intervalMs)
    }, [module, start, intervalMs, timeoutMs, dispatch])

    return { isSyncing, startSync }
}

export default useSyncProcess
